import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, useSeo } from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import StoreReviews from "@/components/StoreReviews";
import StoreContent from "@/components/StoreContent";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Globe, CheckCircle2, Star } from "lucide-react";

const JSONLD_ID = "dv-store-jsonld";

function injectJsonLd(store, reviews, coupons) {
  if (typeof document === "undefined" || !store || !reviews) return;
  const existing = document.getElementById(JSONLD_ID);
  if (existing) existing.remove();

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const storeUrl = `${origin}/store/${store.slug}`;

  const storeSchema = {
    "@context": "https://schema.org",
    "@type": "Store",
    "name": store.name,
    "description": store.description,
    "url": storeUrl,
    "image": store.logo_url || undefined,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": reviews.average,
      "reviewCount": reviews.count,
      "bestRating": 5,
      "worstRating": 1,
    },
    "review": [
      ...(reviews.user_reviews || []).slice(0, 5).map((r) => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.author },
        "datePublished": r.created_at,
        "reviewBody": r.comment,
        "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5, "worstRating": 1 },
      })),
      ...(reviews.recent || []).slice(0, 4).map((r) => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.author },
        "datePublished": r.date,
        "reviewBody": r.comment,
        "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5, "worstRating": 1 },
      })),
    ],
  };

  // Coupons as an ItemList of Offer/Product
  const offerList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${store.name} coupons and promo codes`,
    "url": storeUrl,
    "numberOfItems": coupons.length,
    "itemListElement": coupons.slice(0, 20).map((c, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Offer",
        "name": c.title,
        "description": c.description || c.title,
        "url": storeUrl,
        "seller": { "@type": "Organization", "name": store.name },
        "category": c.category_slug || undefined,
        "availability": "https://schema.org/InStock",
        "validThrough": c.expires_at || undefined,
        "priceCurrency": "USD",
        "price": "0",
        "sku": c.code || c.id,
        "eligibleRegion": "US",
      },
    })),
  };

  const combined = [storeSchema, offerList];
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = JSONLD_ID;
  script.text = JSON.stringify(combined);
  document.head.appendChild(script);
}

export default function StoreDetail() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState(null);
  const [content, setContent] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  const fetchReviews = useCallback(() => {
    api.get(`/stores/${slug}/reviews`).then((r) => setReviews(r.data)).catch(console.error);
  }, [slug]);

  useEffect(() => {
    Promise.all([
      api.get(`/stores/${slug}`),
      api.get("/coupons", { params: { store: slug, limit: 100 } }),
      api.get(`/stores/${slug}/related`, { params: { limit: 6 } }),
      api.get(`/stores/${slug}/reviews`),
      api.get(`/stores/${slug}/content`),
    ]).then(([s, c, r, rv, ct]) => {
      setStore(s.data);
      setCoupons(c.data);
      setRelated(r.data);
      setReviews(rv.data);
      setContent(ct.data);
    }).catch(console.error);

    return () => {
      const el = document.getElementById(JSONLD_ID);
      if (el) el.remove();
    };
  }, [slug]);

  useEffect(() => {
    injectJsonLd(store, reviews, coupons);
  }, [store, reviews, coupons]);

  useSeo(
    store ? (store.seo_title || `${store.name} Coupons & Promo Codes 2026 — SharePromos`) : "Store — SharePromos",
    store
      ? (store.seo_description || `${store.coupon_count || coupons.length} verified ${store.name} coupon codes, promo codes & cashback deals for 2026. Tested daily by real editors. Save on your next ${store.name} order.`)
      : "",
    {
      keywords: store
        ? (store.seo_keywords || `${store.name} coupons, ${store.name} promo code, ${store.name} discount code, ${store.name} coupon code, ${store.name} sale, ${store.name} deals, ${store.name} free shipping, ${store.name} cashback, ${store.name} 2026, ${store.name} promo, ${store.name} coupons today`)
        : undefined,
      image: store?.logo_url || undefined,
    }
  );

  if (!store) return <div className="max-w-7xl mx-auto px-6 py-20">Loading…</div>;

  return (
    <div data-testid="store-detail-page">
      <div className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <Breadcrumbs items={[
            { label: "Stores", to: "/stores" },
            { label: store.name },
          ]} />
          <div className="flex items-start gap-8 flex-wrap md:flex-nowrap">
            <div data-testid="store-logo-tile" className="w-24 h-24 border border-slate-200 bg-white flex items-center justify-center p-4 rounded-sm shrink-0">
              {store.logo_url && !logoFailed ? (
                <img data-testid="store-logo-image" src={store.logo_url} alt={`${store.name} store logo — verified coupons on SharePromos`} className="max-w-full max-h-full object-contain"
                     onError={() => setLogoFailed(true)} />
              ) : (
                <span data-testid="store-logo-fallback" className="font-display text-4xl">{store.name[0]}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="overline mb-2 text-emerald-600">
                <CheckCircle2 className="w-3 h-3 inline mr-1" /> {coupons.length} active offers
              </p>
              <h1 className="font-display text-4xl md:text-6xl tracking-tighter uppercase mb-3">
                {store.name} Coupons
              </h1>
              <p className="text-slate-600 leading-relaxed max-w-2xl">{store.description}</p>
              <div className="flex flex-wrap items-center gap-6 mt-5">
                {reviews && (
                  <div className="flex items-center gap-2" data-testid="hero-rating">
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i}
                              className={`w-4 h-4 ${i < Math.round(reviews.average) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
                              strokeWidth={1.5} />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-slate-950">{reviews.average.toFixed(1)}</span>
                    <span className="text-xs text-slate-500">({reviews.count.toLocaleString()} reviews)</span>
                  </div>
                )}
                {store.website && (
                  <a href={`${API}/go/store/${store.slug}`}
                     target="_blank" rel="noopener noreferrer"
                     data-testid="visit-store-link"
                     className="inline-flex items-center gap-2 text-sm text-slate-950 link-underline">
                    <Globe className="w-4 h-4" /> Visit {store.name}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-6">
          Active {store.name} promo codes ({coupons.length})
        </h2>
        <div className="grid grid-cols-1 gap-3">
          {coupons.map((c) => <CouponCard key={c.id} coupon={c} showStore={false} />)}
        </div>
        {coupons.length === 0 && <p className="text-slate-500">No active coupons yet — check back soon.</p>}

        {/* SEO content section */}
        <StoreContent content={content} storeName={store.name} />

        {/* Automated review section */}
        <StoreReviews
          reviews={reviews}
          storeName={store.name}
          storeSlug={store.slug}
          onReviewPosted={fetchReviews}
        />

        {related.length > 0 && (
          <section className="mt-20 border-t border-slate-200 pt-16" data-testid="related-section">
            <div className="mb-8">
              <p className="overline mb-3">Discover more</p>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight">
                Similar deals to {store.name}
              </h2>
              <p className="text-sm text-slate-500 mt-2">
                Popular offers from other stores in the same category.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {related.map((c) => <CouponCard key={c.id} coupon={c} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
